import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronDown,
  ChevronRight as ExpandRight,
  ChevronRight,
  KeyRound,
  LogOut,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Stethoscope,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { type CSSProperties, type Dispatch, type FormEvent, type HTMLAttributes, type SetStateAction, useEffect, useMemo, useState } from "react";
import { adminRequest, createRecord, loginAdmin } from "../lib/api";
import { buddhistInputToIso, isoToBuddhistInput, todayBuddhistInputValue } from "../lib/format";
import { normalizeNumericIdentifier } from "../shared/normalization";
import type { AdminRecordPayload, AdminSession, PrivilegeStatus } from "../shared/types";

interface TierRow {
  id: string;
  tier_code: string;
  tier_name: string;
  display_order: number;
  donation_min: number;
  donation_max: number | null;
  donation_label: string;
  room_discount_under_3000: number;
  room_discount_3000_up: number;
  parking_eligible: boolean;
  parking_policy: string;
  entitlement_scope: string;
  benefit_duration_label: string;
  primary_lifetime: boolean;
  supplementary_years: number | null;
  supplementary_limit: number;
  pmc_eligible: boolean;
  pmc_fee_waiver_amount: number | null;
  pmc_annual_limit: number | null;
  color_bg: string;
  color_border: string;
  color_text: string;
}

type TierSummary = Pick<TierRow, "id" | "tier_name" | "color_bg" | "color_border" | "color_text">;

interface PersonRow {
  id: string;
  hn: string | null;
  first_name: string;
  last_name: string;
  dob: string | null;
  phone: string | null;
  national_id_last4: string | null;
  is_deceased: boolean;
  notes: string | null;
  created_at: string;
  donor_role?: "primary" | "supplementary" | null;
  tier?: TierSummary | null;
}

interface DetailedPrivilege {
  id: string;
  tier_id: string;
  donation_total: number | null;
  benefit_start_date: string;
  benefit_end_date: string | null;
  status: PrivilegeStatus;
  notes: string | null;
  tier: TierRow | null;
  members: Array<{
    id: string;
    role: "primary" | "supplementary";
    benefit_start_date: string | null;
    benefit_end_date: string | null;
    person: Partial<PersonRow> | null;
  }>;
  pmcUsage: {
    eligible: boolean;
    feeWaiverAmount: number | null;
    annualLimit: number | null;
    usedThisYear: number;
    remainingUses: number | null;
    latestUsedAt: string | null;
    records: Array<{ id: string; used_at: string; created_at: string }>;
  };
}

interface SupplementaryAssociation {
  membershipId: string;
  privilegeId: string;
  benefitStartDate: string | null;
  benefitEndDate: string | null;
  primaryPerson: Partial<PersonRow> | null;
  tier: TierSummary | null;
}

interface DetailedRecord {
  person: PersonRow;
  privileges: DetailedPrivilege[];
  supplementaryAssociations: SupplementaryAssociation[];
}

interface GroupedRecord {
  privilegeId: string;
  primaryPerson: PersonRow;
  tier: TierRow | null;
  donationTotal: number | null;
  benefitStartDate: string;
  benefitEndDate: string | null;
  status: PrivilegeStatus;
  supplementaryMembers: Array<{
    id: string;
    person_id: string;
    person: Partial<PersonRow> | null;
  }>;
}

interface DraftState {
  hn: string;
  firstName: string;
  lastName: string;
  dob: string;
  phone: string;
  nationalId: string;
  isDeceased: boolean;
  notes: string;
  tierId: string;
  donationTotal: string;
  benefitStartDate: string;
  benefitEndDate: string;
  status: PrivilegeStatus;
  privilegeNotes: string;
}

interface AdminDisclosureState {
  personal: boolean;
  privilege: boolean;
  supplementary: boolean;
  tiers: boolean;
}

const newRecordDisclosures: AdminDisclosureState = {
  personal: true,
  privilege: false,
  supplementary: false,
  tiers: false,
};

function disclosuresForRecord(record: DetailedRecord): AdminDisclosureState {
  const hasPrimaryPrivilege = record.privileges.length > 0;
  return {
    personal: !hasPrimaryPrivilege,
    privilege: hasPrimaryPrivilege,
    supplementary: false,
    tiers: false,
  };
}

const emptyDraft: DraftState = {
  hn: "",
  firstName: "",
  lastName: "",
  dob: "",
  phone: "",
  nationalId: "",
  isDeceased: false,
  notes: "",
  tierId: "",
  donationTotal: "",
  benefitStartDate: todayBuddhistInputValue(),
  benefitEndDate: "",
  status: "active",
  privilegeNotes: "",
};

function loadSession(): AdminSession | null {
  const raw = sessionStorage.getItem("adminSession");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminSession;
  } catch {
    sessionStorage.removeItem("adminSession");
    return null;
  }
}

export function AdminApp() {
  const [session, setSession] = useState<AdminSession | null>(loadSession);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  async function handleLogin(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      const nextSession = await loginAdmin(username, password);
      sessionStorage.setItem("adminSession", JSON.stringify(nextSession));
      setSession(nextSession);
    } catch (loginError) {
      setError((loginError as Error).message);
    }
  }

  if (!session) {
    return (
      <section className="admin-login">
        <form className="login-panel" onSubmit={handleLogin}>
          <ShieldCheck size={34} />
          <h1>เข้าสู่ระบบผู้ดูแล</h1>
          <label>
            <span>ชื่อผู้ใช้</span>
            <input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} />
          </label>
          <label>
            <span>รหัสผ่าน</span>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="primary-action" type="submit">
            <ShieldCheck size={18} />
            เข้าสู่ระบบ
          </button>
        </form>
      </section>
    );
  }

  return <AdminWorkspace session={session} onLogout={() => setSession(null)} />;
}

function AdminWorkspace({ session, onLogout }: { session: AdminSession; onLogout: () => void }) {
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [records, setRecords] = useState<GroupedRecord[]>([]);
  const [expandedRecords, setExpandedRecords] = useState<Set<string>>(() => new Set());
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selected, setSelected] = useState<DetailedRecord | null>(null);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isConvertingToPrimary, setIsConvertingToPrimary] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [openSections, setOpenSections] = useState<AdminDisclosureState>(newRecordDisclosures);
  const [memberDraft, setMemberDraft] = useState({
    firstName: "",
    lastName: "",
    hn: "",
    nationalId: "",
    phone: "",
    dob: "",
  });
  const [pmcUsedAt, setPmcUsedAt] = useState(todayBuddhistInputValue());
  const [pmcSaving, setPmcSaving] = useState(false);

  const canWrite = session.profile.role !== "viewer";
  const activePrivilege = selected?.privileges[0] ?? null;
  const isSupplementaryOnly = Boolean(selected && !activePrivilege && selected.supplementaryAssociations.length);
  const displayRole = activePrivilege ? "primary" : isSupplementaryOnly ? "supplementary" : selected?.person.donor_role ?? null;
  const displayTier = activePrivilege?.tier ?? selected?.supplementaryAssociations[0]?.tier ?? selected?.person.tier ?? null;

  async function loadBootstrap() {
    const data = await adminRequest<{ ok: boolean; tiers: TierRow[] }>(session.accessToken, "bootstrap");
    setTiers(data.tiers);
  }

  async function loadRecords(nextQuery = query, nextPage = page) {
    const data = await adminRequest<{ ok: boolean; records: GroupedRecord[]; page: number; hasNext: boolean }>(
      session.accessToken,
      `records?page=${nextPage}&q=${encodeURIComponent(nextQuery)}`,
    );
    setRecords(data.records);
    setPage(data.page);
    setHasNextPage(data.hasNext);
  }

  async function loadDetail(personId: string) {
    const data = await adminRequest<{ ok: boolean; record: DetailedRecord }>(
      session.accessToken,
      `records/${personId}`,
    );
    setSelected(data.record);
    setDraft(fromRecord(data.record));
    setIsConvertingToPrimary(false);
    setOpenSections(disclosuresForRecord(data.record));
    setPmcUsedAt(todayBuddhistInputValue());
  }

  useEffect(() => {
    loadBootstrap()
      .then(() => loadRecords(""))
      .catch((loadError) => setError((loadError as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTier = useMemo(
    () => tiers.find((tier) => tier.id === draft.tierId) ?? null,
    [draft.tierId, tiers],
  );
  const supplementaryMembers = activePrivilege?.members.filter((member) => member.role === "supplementary") ?? [];
  const supplementaryLimit = selectedTier?.supplementary_limit ?? activePrivilege?.tier?.supplementary_limit ?? 0;
  const excessSupplementaryMembers = supplementaryMembers.slice(supplementaryLimit);
  const canAddSupplementary = Boolean(activePrivilege && supplementaryLimit > 0 && supplementaryMembers.length < supplementaryLimit);

  async function handleSearch(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await loadRecords(query, 1);
    } catch (searchError) {
      setError((searchError as Error).message);
    }
  }

  function updateDonationDraft(value: string) {
    setDraft((current) => applyPrivilegeInference({ ...current, donationTotal: value }, tiers));
  }

  function updateBenefitStartDate(value: string) {
    setDraft((current) => applyPrivilegeInference({ ...current, benefitStartDate: value }, tiers));
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setError("");
    setStatus("");
    const validation = validateDraft(draft, Boolean(activePrivilege || isConvertingToPrimary || !selected), tiers);
    setFieldErrors(validation.errors);
    if (!validation.ok) {
      const personFields = ["hn", "firstName", "lastName", "dob", "phone", "nationalId"];
      const privilegeFields = ["donationTotal", "benefitStartDate", "benefitEndDate"];
      setOpenSections((current) => ({
        ...current,
        personal: current.personal || personFields.some((field) => Boolean(validation.errors[field])),
        privilege: current.privilege || privilegeFields.some((field) => Boolean(validation.errors[field])),
      }));
      setError("กรุณาตรวจสอบข้อมูลที่ไฮไลต์");
      return;
    }
    const payload = toRecordPayload(draft);
    if (!payload) {
      setError("รูปแบบวันที่ไม่ถูกต้อง");
      return;
    }
    if (excessSupplementaryMembers.length) {
      const names = excessSupplementaryMembers
        .map((member) => `${member.person?.first_name ?? ""} ${member.person?.last_name ?? ""}`.trim() || "ไม่ระบุชื่อ")
        .join(", ");
      const confirmed = window.confirm(`เมื่อบันทึก ระบบจะถอดผู้บริจาคสมทบส่วนเกิน: ${names}`);
      if (!confirmed) return;
    }
    try {
      if (selected) {
        let data: { ok: boolean; record: DetailedRecord };
        if (isConvertingToPrimary) {
          if (!payload.privilege) {
            setError("กรุณาระบุยอดบริจาคเพื่อสร้างสิทธิหลัก");
            return;
          }
          await adminRequest(session.accessToken, `records/${selected.person.id}`, {
            method: "PATCH",
            body: JSON.stringify({ person: payload.person }),
          });
          data = await adminRequest<{ ok: boolean; record: DetailedRecord }>(
            session.accessToken,
            `records/${selected.person.id}/convert-primary`,
            {
              method: "POST",
              body: JSON.stringify(payload.privilege),
            },
          );
        } else {
          data = await adminRequest<{ ok: boolean; record: DetailedRecord }>(
            session.accessToken,
            `records/${selected.person.id}`,
            {
              method: "PATCH",
              body: JSON.stringify({
                person: payload.person,
                privilege: activePrivilege && payload.privilege
                  ? {
                      id: activePrivilege.id,
                      ...payload.privilege,
                    }
                  : undefined,
              }),
            },
          );
          if (!activePrivilege && payload.privilege) {
            data = await adminRequest<{ ok: boolean; record: DetailedRecord }>(
              session.accessToken,
              `records/${selected.person.id}/privileges`,
              {
                method: "POST",
                body: JSON.stringify(payload.privilege),
              },
            );
          }
        }
        setSelected(data.record);
        setDraft(fromRecord(data.record));
        setIsConvertingToPrimary(false);
        if (isConvertingToPrimary) setOpenSections(disclosuresForRecord(data.record));
      } else {
        const data = await createRecord(session.accessToken, payload);
        setSelected(data.record as DetailedRecord);
        setDraft(fromRecord(data.record as DetailedRecord));
        setIsConvertingToPrimary(false);
        setOpenSections(disclosuresForRecord(data.record as DetailedRecord));
      }
      await loadRecords(query);
      setStatus("บันทึกข้อมูลแล้ว");
    } catch (saveError) {
      setError((saveError as Error).message);
    }
  }

  async function handleDelete() {
    if (!selected || !canWrite) return;
    setError("");
    try {
      await adminRequest(session.accessToken, `records/${selected.person.id}`, { method: "DELETE" });
      setSelected(null);
      setDraft(emptyDraft);
      setIsConvertingToPrimary(false);
      setOpenSections(newRecordDisclosures);
      await loadRecords(query);
      setStatus("ลบข้อมูลแล้ว");
    } catch (deleteError) {
      setError((deleteError as Error).message);
    }
  }

  async function handleAddMember(event: FormEvent) {
    event.preventDefault();
    if (!selected || !activePrivilege) return;
    const nextErrors = validateMemberDraft(memberDraft);
    setFieldErrors((current) => ({ ...current, ...nextErrors }));
    if (Object.keys(nextErrors).length) {
      setOpenSections((current) => ({ ...current, supplementary: true }));
      setError("กรุณาตรวจสอบข้อมูลผู้บริจาคสมทบ");
      return;
    }
    if (!canAddSupplementary) {
      setError("ระดับสิทธินี้ไม่สามารถเพิ่มผู้บริจาคสมทบได้อีก");
      return;
    }
    try {
      const data = await adminRequest<{ ok: boolean; record: DetailedRecord }>(
        session.accessToken,
        `privileges/${activePrivilege.id}/members`,
        {
          method: "POST",
          body: JSON.stringify({
            person: {
              firstName: memberDraft.firstName,
              lastName: memberDraft.lastName,
              hn: memberDraft.hn,
              nationalId: memberDraft.nationalId,
              phone: memberDraft.phone,
              dob: buddhistInputToIso(memberDraft.dob) ?? undefined,
            },
          }),
        },
      );
      setMemberDraft({ firstName: "", lastName: "", hn: "", nationalId: "", phone: "", dob: "" });
      setSelected(data.record);
      setDraft(fromRecord(data.record));
      await loadRecords(query);
    } catch (memberError) {
      setError((memberError as Error).message);
    }
  }

  async function handleRecordPmcUsage(event: FormEvent) {
    event.preventDefault();
    if (!selected || !activePrivilege || !canWrite) return;
    const usedAt = buddhistInputToIso(pmcUsedAt);
    if (!usedAt) {
      setError("กรุณาระบุวันที่ใช้สิทธิ PMC ให้ถูกต้อง");
      return;
    }
    setError("");
    setStatus("");
    setPmcSaving(true);
    try {
      const data = await adminRequest<{ ok: boolean; record: DetailedRecord }>(
        session.accessToken,
        `privileges/${activePrivilege.id}/pmc-usage`,
        { method: "POST", body: JSON.stringify({ usedAt }) },
      );
      setSelected(data.record);
      setPmcUsedAt(todayBuddhistInputValue());
      setStatus("บันทึกการใช้สิทธิ PMC แล้ว");
    } catch (pmcError) {
      setError((pmcError as Error).message);
    } finally {
      setPmcSaving(false);
    }
  }

  async function handleDeletePmcUsage(usageId: string) {
    if (!selected || !canWrite) return;
    try {
      await adminRequest(session.accessToken, `pmc-usage/${usageId}`, { method: "DELETE" });
      await loadDetail(selected.person.id);
      setStatus("ยกเลิกรายการใช้สิทธิ PMC แล้ว");
    } catch (pmcError) {
      setError((pmcError as Error).message);
    }
  }

  async function handleChangePassword(event: FormEvent) {
    event.preventDefault();
    setError("");
    setStatus("");
    if (newPassword.length < 8) {
      setError("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("รหัสผ่านใหม่ไม่ตรงกัน");
      return;
    }
    try {
      await adminRequest(session.accessToken, "password", {
        method: "POST",
        body: JSON.stringify({ password: newPassword }),
      });
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
      setStatus("เปลี่ยนรหัสผ่านแล้ว");
    } catch (passwordError) {
      setError((passwordError as Error).message);
    }
  }

  function logout() {
    sessionStorage.removeItem("adminSession");
    onLogout();
  }

  return (
    <section className="admin-layout">
      <aside className="admin-sidebar">
        <section className="record-sidebar">
          <div className="admin-toolbar">
            <div>
              <h1>ทะเบียนผู้บริจาค</h1>
              <p>{session.profile.displayName || session.user.email}</p>
            </div>
          </div>

          <form className="search-row" onSubmit={handleSearch}>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ค้นหา ชื่อ / HN" />
            <button className="icon-button" type="submit" title="ค้นหา">
              <Search size={18} />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={() => loadRecords(query, page)}
              title="โหลดใหม่"
            >
              <RefreshCw size={18} />
            </button>
          </form>

          <button
            className="secondary-action full"
            type="button"
            onClick={() => {
              setSelected(null);
              setDraft({ ...emptyDraft, tierId: "" });
              setIsConvertingToPrimary(false);
              setOpenSections(newRecordDisclosures);
            }}
          >
            <Plus size={18} />
            เพิ่มผู้บริจาค
          </button>

          <div className="record-list">
            {records.map((record) => {
              const expanded = expandedRecords.has(record.privilegeId);
              return (
                <div className="record-group" key={record.privilegeId}>
                  <button
                    className={selected?.person.id === record.primaryPerson.id ? "record-row active" : "record-row"}
                    type="button"
                    onClick={() => loadDetail(record.primaryPerson.id).catch((detailError) => setError((detailError as Error).message))}
                  >
                    <span className="record-title-line">
                      <span
                        className="expand-toggle"
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedRecords((current) => toggleSetValue(current, record.privilegeId));
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            setExpandedRecords((current) => toggleSetValue(current, record.privilegeId));
                          }
                        }}
                        title={expanded ? "ย่อรายการสมทบ" : "ดูผู้บริจาคสมทบ"}
                      >
                        {expanded ? <ChevronDown size={16} /> : <ExpandRight size={16} />}
                      </span>
                      <strong>
                        {record.primaryPerson.first_name} {record.primaryPerson.last_name}
                      </strong>
                    </span>
                    <span className="record-meta">
                      {record.primaryPerson.hn || "ไม่มี HN"}
                      <span className="record-chip-row">
                        <RoleChip role="primary" />
                        <TierChip tier={record.tier} />
                        <small className="count-chip">สมทบ {record.supplementaryMembers.length}/{record.tier?.supplementary_limit ?? 0}</small>
                      </span>
                    </span>
                  </button>
                  {expanded ? (
                    <div className="nested-record-list">
                      {record.supplementaryMembers.length ? record.supplementaryMembers.map((member) => (
                        <button
                          className={selected?.person.id === member.person_id ? "record-row nested active" : "record-row nested"}
                          key={member.id}
                          type="button"
                          onClick={() => loadDetail(member.person_id).catch((detailError) => setError((detailError as Error).message))}
                        >
                          <strong>
                            {member.person?.first_name} {member.person?.last_name}
                          </strong>
                          <span className="record-meta">
                            {member.person?.hn || "ไม่มี HN"}
                            <span className="record-chip-row">
                              <RoleChip role="supplementary" />
                              <TierChip tier={record.tier} />
                            </span>
                          </span>
                        </button>
                      )) : <p className="nested-empty">ยังไม่มีผู้บริจาคสมทบ</p>}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="pagination-row" aria-label="หน้า">
            <button
              className="icon-button"
              type="button"
              disabled={page <= 1}
              onClick={() => loadRecords(query, page - 1).catch((pageError) => setError((pageError as Error).message))}
              title="หน้าก่อนหน้า"
            >
              <ChevronLeft size={18} />
            </button>
            <span>หน้า {page}</span>
            <button
              className="icon-button"
              type="button"
              disabled={!hasNextPage}
              onClick={() => loadRecords(query, page + 1).catch((pageError) => setError((pageError as Error).message))}
              title="หน้าถัดไป"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </section>
      </aside>

      <div className="admin-detail">
        {error ? (
          <AdminFeedbackModal
            kind="error"
            message={error}
            fieldErrors={fieldErrors}
            onClose={() => setError("")}
          />
        ) : null}
        {status ? (
          <AdminFeedbackModal
            kind="success"
            message={status}
            fieldErrors={{}}
            onClose={() => setStatus("")}
          />
        ) : null}

        <form className="editor-panel" onSubmit={handleSave}>
          <div className="section-heading">
            <div>
              <p className="eyebrow">{selected ? "แก้ไขข้อมูล" : "รายการใหม่"}</p>
              <div className="donor-heading">
                <h2>{selected ? `${selected.person.first_name} ${selected.person.last_name}` : "เพิ่มผู้บริจาค"}</h2>
                {selected ? (
                  <span className="donor-chip-row">
                    <RoleChip role={displayRole} />
                    <TierChip tier={displayTier} />
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <section className="form-disclosure">
            <DisclosureButton
              title="ข้อมูลบุคคล"
              open={openSections.personal}
              onToggle={() => setOpenSections((current) => ({ ...current, personal: !current.personal }))}
            />
            {openSections.personal ? (
            <fieldset disabled={!canWrite} aria-label="ข้อมูลบุคคล">
            <div className="form-grid">
              <Input label="HN" value={draft.hn} error={fieldErrors.hn} inputMode="numeric" onChange={(value) => setDraftField(setDraft, "hn", value)} />
              <Input label="ชื่อ" value={draft.firstName} error={fieldErrors.firstName} onChange={(value) => setDraftField(setDraft, "firstName", value)} required />
              <Input label="นามสกุล" value={draft.lastName} error={fieldErrors.lastName} onChange={(value) => setDraftField(setDraft, "lastName", value)} required />
              <DateInput label="วันเกิด" value={draft.dob} error={fieldErrors.dob} onChange={(value) => setDraftField(setDraft, "dob", value)} />
              <Input label="เบอร์โทร" value={draft.phone} error={fieldErrors.phone} inputMode="tel" onChange={(value) => setDraftField(setDraft, "phone", value)} />
              <Input
                label={selected?.person.national_id_last4 ? `เลขบัตรประชาชนใหม่ (เดิมลงท้าย ${selected.person.national_id_last4})` : "เลขบัตรประชาชน"}
                value={draft.nationalId}
                error={fieldErrors.nationalId}
                inputMode="numeric"
                onChange={(value) => setDraftField(setDraft, "nationalId", value)}
              />
              <label className="checkline">
                <input
                  type="checkbox"
                  checked={draft.isDeceased}
                  onChange={(event) => setDraft((current) => ({ ...current, isDeceased: event.target.checked }))}
                />
                <span>เสียชีวิตแล้ว</span>
              </label>
            </div>
            <label>
              <span>หมายเหตุ</span>
              <textarea value={draft.notes} onChange={(event) => setDraftField(setDraft, "notes", event.target.value)} />
            </label>
            </fieldset>
            ) : null}
          </section>

          {isSupplementaryOnly && !isConvertingToPrimary ? (
            <section className="association-panel">
              <div>
                <p className="eyebrow">ผู้บริจาคสมทบ</p>
                <h3>เชื่อมกับสิทธิหลักอยู่</h3>
              </div>
              {selected?.supplementaryAssociations.map((association) => (
                <div className="association-row" key={association.membershipId}>
                  <div>
                    <strong>
                      {association.primaryPerson?.first_name} {association.primaryPerson?.last_name}
                    </strong>
                    <span>{association.primaryPerson?.hn || "ไม่มี HN"}</span>
                  </div>
                  <TierChip tier={association.tier} />
                </div>
              ))}
              <button
                className="secondary-action compact"
                type="button"
                disabled={!canWrite}
                onClick={() => {
                  setIsConvertingToPrimary(true);
                  setOpenSections((current) => ({ ...current, privilege: true }));
                  setDraft((current) => ({
                    ...current,
                    tierId: "",
                    donationTotal: "",
                    benefitStartDate: todayBuddhistInputValue(),
                    benefitEndDate: "",
                    status: "active",
                    privilegeNotes: "",
                  }));
                }}
              >
                <Plus size={17} />
                เปลี่ยนเป็นผู้บริจาคหลัก
              </button>
            </section>
          ) : null}

          {!isSupplementaryOnly || isConvertingToPrimary ? (
          <section className="form-disclosure">
            <DisclosureButton
              title="สิทธิประโยชน์หลัก"
              open={openSections.privilege}
              onToggle={() => setOpenSections((current) => ({ ...current, privilege: !current.privilege }))}
            />
            {openSections.privilege ? (
          <fieldset disabled={!canWrite} aria-label="สิทธิประโยชน์หลัก">
            {isConvertingToPrimary ? (
              <div className="conversion-note">
                เมื่อบันทึก ระบบจะสร้างสิทธิหลักใหม่ และยกเลิกการเป็นผู้บริจาคสมทบเดิมทั้งหมด
              </div>
            ) : null}
            <div className="form-grid">
              <label>
                <span>ระดับสิทธิ (คำนวณจากยอดบริจาค)</span>
                <select value={draft.tierId} disabled>
                  {tiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.tier_name} · {tier.donation_label}
                    </option>
                  ))}
                </select>
              </label>
              <Input label="ยอดบริจาค" type="number" value={draft.donationTotal} error={fieldErrors.donationTotal} onChange={(value) => updateDonationDraft(value)} required />
              <DateInput label="วันที่เริ่มสิทธิ" value={draft.benefitStartDate} error={fieldErrors.benefitStartDate} onChange={(value) => updateBenefitStartDate(value)} required />
              <DateInput label="วันที่สิ้นสุด (คำนวณอัตโนมัติ)" value={draft.benefitEndDate} error={fieldErrors.benefitEndDate} onChange={(value) => setDraftField(setDraft, "benefitEndDate", value)} />
              <label>
                <span>สถานะ</span>
                <select value={draft.status} onChange={(event) => setDraftField(setDraft, "status", event.target.value as PrivilegeStatus)}>
                  <option value="active">ใช้งาน</option>
                  <option value="suspended">พักสิทธิ</option>
                  <option value="expired">หมดอายุ</option>
                </select>
              </label>
            </div>
            {selectedTier ? (
              <div className="tier-note">
                <strong>{selectedTier.tier_name}</strong>
                <span>ที่จอดรถ: {selectedTier.parking_policy}</span>
                <span>ระยะเวลาสิทธิ: {selectedTier.benefit_duration_label}</span>
                <span>PMC: {formatPmcPolicy(selectedTier)}</span>
              </div>
            ) : null}
            <label>
              <span>หมายเหตุสิทธิ</span>
              <textarea value={draft.privilegeNotes} onChange={(event) => setDraftField(setDraft, "privilegeNotes", event.target.value)} />
            </label>
          </fieldset>
            ) : null}
          </section>
          ) : null}
          <div className="form-actions-bottom">
            {selected && canWrite ? (
              <button className="danger-action" type="button" onClick={handleDelete}>
                <Trash2 size={17} />
                ลบ
              </button>
            ) : null}
            <button className="primary-action compact" type="submit" disabled={!canWrite}>
              <Save size={17} />
              บันทึก
            </button>
          </div>
        </form>

        {selected && activePrivilege ? (
          <div className="detail-grid">
            <section className="detail-panel pmc-admin-panel">
              <div className="pmc-panel-heading">
                <span className="modal-icon info"><Stethoscope size={20} /></span>
                <div>
                  <strong>การใช้สิทธิห้อง PMC</strong>
                  <small>{formatPmcPolicy(activePrivilege.tier)}</small>
                </div>
              </div>
              {activePrivilege.pmcUsage.eligible ? (
                <>
                  <div className="pmc-summary-grid">
                    <div>
                      <span>คงเหลือปีนี้</span>
                      <strong>{activePrivilege.pmcUsage.remainingUses === null ? "ไม่จำกัด" : `${activePrivilege.pmcUsage.remainingUses} ครั้ง`}</strong>
                    </div>
                    <div>
                      <span>ใช้ล่าสุด</span>
                      <strong>{activePrivilege.pmcUsage.latestUsedAt ? isoToBuddhistInput(activePrivilege.pmcUsage.latestUsedAt) : "ยังไม่เคยใช้"}</strong>
                    </div>
                  </div>
                  <form className="pmc-usage-form" onSubmit={handleRecordPmcUsage}>
                    <DateInput label="วันที่ใช้สิทธิ" value={pmcUsedAt} onChange={setPmcUsedAt} required />
                    <button className="secondary-action compact" type="submit" disabled={!canWrite || pmcSaving || activePrivilege.pmcUsage.remainingUses === 0}>
                      <Plus size={17} />
                      {pmcSaving ? "กำลังบันทึก" : "บันทึกการใช้"}
                    </button>
                  </form>
                  {activePrivilege.pmcUsage.records.length ? (
                    <div className="pmc-usage-list" aria-label="รายการใช้ PMC ล่าสุด">
                      {activePrivilege.pmcUsage.records.map((usage) => (
                        <div key={usage.id}>
                          <span>{isoToBuddhistInput(usage.used_at)}</span>
                          <button className="icon-button" type="button" title="ยกเลิกรายการ" aria-label={`ยกเลิกรายการวันที่ ${isoToBuddhistInput(usage.used_at)}`} onClick={() => handleDeletePmcUsage(usage.id)} disabled={!canWrite}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="disabled-note">ระดับสิทธินี้ไม่มีสิทธิใช้บริการห้อง PMC</div>
              )}
            </section>
            <section className="detail-panel">
              <DisclosureButton
                title="ผู้บริจาคสมทบ"
                meta={`ใช้ได้ ${supplementaryMembers.length} / ${supplementaryLimit}`}
                open={openSections.supplementary}
                onToggle={() => setOpenSections((current) => ({ ...current, supplementary: !current.supplementary }))}
              />
              {openSections.supplementary ? (
              <div className="disclosure-content">
              {supplementaryLimit ? (
                <form className="member-form" onSubmit={handleAddMember}>
                  <Input label="ชื่อ" value={memberDraft.firstName} error={fieldErrors.memberFirstName} onChange={(value) => setMemberDraft((current) => ({ ...current, firstName: value }))} required />
                  <Input label="นามสกุล" value={memberDraft.lastName} error={fieldErrors.memberLastName} onChange={(value) => setMemberDraft((current) => ({ ...current, lastName: value }))} required />
                  <Input label="HN" value={memberDraft.hn} error={fieldErrors.memberHn} inputMode="numeric" onChange={(value) => setMemberDraft((current) => ({ ...current, hn: value }))} />
                  <Input label="เลขบัตรประชาชน" value={memberDraft.nationalId} error={fieldErrors.memberNationalId} inputMode="numeric" onChange={(value) => setMemberDraft((current) => ({ ...current, nationalId: value }))} />
                  <Input label="เบอร์โทร" value={memberDraft.phone} error={fieldErrors.memberPhone} inputMode="tel" onChange={(value) => setMemberDraft((current) => ({ ...current, phone: value }))} />
                  <DateInput label="วันเกิด" value={memberDraft.dob} error={fieldErrors.memberDob} onChange={(value) => setMemberDraft((current) => ({ ...current, dob: value }))} />
                  <button className="secondary-action full" type="submit" disabled={!canWrite || !canAddSupplementary}>
                    <UserPlus size={17} />
                    เพิ่มผู้บริจาคสมทบ
                  </button>
                </form>
              ) : (
                <div className="disabled-note">ระดับสิทธินี้ยังไม่มีสิทธิผู้บริจาคสมทบ</div>
              )}
              <div className="member-list">
                {activePrivilege.members
                  .filter((member) => member.role === "supplementary")
                  .map((member, index) => (
                    <div className={index >= supplementaryLimit ? "member-row pending-removal" : "member-row"} key={member.id}>
                      <strong>
                        {member.person?.first_name} {member.person?.last_name}
                      </strong>
                      <span>{index >= supplementaryLimit ? "จะถูกถอดเมื่อบันทึก" : member.person?.hn || "ไม่มี HN"}</span>
                    </div>
                  ))}
              </div>
              </div>
              ) : null}
            </section>
          </div>
        ) : null}

        <TierReference
          tiers={tiers}
          open={openSections.tiers}
          onToggle={() => setOpenSections((current) => ({ ...current, tiers: !current.tiers }))}
        />
        <section className="account-panel">
          <strong>บัญชีผู้ใช้</strong>
          <button className="secondary-action full" type="button" onClick={() => setShowPasswordForm((current) => !current)}>
            <KeyRound size={17} />
            เปลี่ยนรหัสผ่าน
          </button>
          <button className="danger-action full" type="button" onClick={logout}>
            <LogOut size={17} />
            ออกจากระบบ
          </button>
          {showPasswordForm ? (
            <form className="password-panel" onSubmit={handleChangePassword}>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="รหัสผ่านใหม่"
              />
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="ยืนยันรหัสผ่าน"
              />
              <button className="primary-action compact" type="submit">
                บันทึก
              </button>
            </form>
          ) : null}
        </section>
      </div>
    </section>
  );
}

function fromRecord(record: DetailedRecord): DraftState {
  const privilege = record.privileges[0];
  return {
    hn: record.person.hn ?? "",
    firstName: record.person.first_name,
    lastName: record.person.last_name,
    dob: isoToBuddhistInput(record.person.dob),
    phone: record.person.phone ?? "",
    nationalId: "",
    isDeceased: record.person.is_deceased,
    notes: record.person.notes ?? "",
    tierId: privilege?.tier_id ?? "",
    donationTotal: privilege?.donation_total ? String(privilege.donation_total) : "",
    benefitStartDate: isoToBuddhistInput(privilege?.benefit_start_date) || todayBuddhistInputValue(),
    benefitEndDate: isoToBuddhistInput(privilege?.benefit_end_date),
    status: privilege?.status ?? "active",
    privilegeNotes: privilege?.notes ?? "",
  };
}

function toRecordPayload(draft: DraftState): AdminRecordPayload | null {
  const dob = draft.dob ? buddhistInputToIso(draft.dob) : "";
  const benefitStartDate = buddhistInputToIso(draft.benefitStartDate);
  const benefitEndDate = draft.benefitEndDate ? buddhistInputToIso(draft.benefitEndDate) : null;
  if (draft.benefitStartDate && !benefitStartDate) return null;
  if (draft.benefitEndDate && !benefitEndDate) return null;
  if (draft.dob && !dob) return null;
  return {
    person: {
      hn: draft.hn,
      firstName: draft.firstName,
      lastName: draft.lastName,
      dob: dob || undefined,
      phone: draft.phone,
      nationalId: draft.nationalId,
      isDeceased: draft.isDeceased,
      notes: draft.notes,
    },
    privilege: draft.tierId
      ? {
          tierId: draft.tierId,
          donationTotal: draft.donationTotal ? Number(draft.donationTotal) : undefined,
          benefitStartDate: benefitStartDate ?? "",
          benefitEndDate,
          status: draft.status,
          notes: draft.privilegeNotes,
        }
      : undefined,
  };
}

function tierForDonation(tiers: TierRow[], donationTotal: string): TierRow | null {
  const amount = Number(donationTotal);
  if (!donationTotal || Number.isNaN(amount)) return null;
  return tiers.find((tier) => (
    amount >= Number(tier.donation_min) &&
    (tier.donation_max === null || amount <= Number(tier.donation_max))
  )) ?? null;
}

function addYearsMinusOneDay(date: string, years: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const next = new Date(Date.UTC(year + years, month - 1, day));
  next.setUTCDate(next.getUTCDate() - 1);
  return next.toISOString().slice(0, 10);
}

function benefitEndDateForTier(tier: TierRow | null, benefitStartDate: string): string {
  if (!tier || !benefitStartDate || tier.primary_lifetime) return "";
  const isoStart = buddhistInputToIso(benefitStartDate);
  if (!isoStart) return "";
  const years = Number(tier.benefit_duration_label.match(/\d+/)?.[0] ?? 0);
  return years > 0 ? isoToBuddhistInput(addYearsMinusOneDay(isoStart, years)) : "";
}

function applyPrivilegeInference(draft: DraftState, tiers: TierRow[]): DraftState {
  const tier = tierForDonation(tiers, draft.donationTotal);
  if (!tier) return { ...draft, tierId: "", benefitEndDate: "" };
  return {
    ...draft,
    tierId: tier.id,
    benefitEndDate: benefitEndDateForTier(tier, draft.benefitStartDate),
  };
}

function validateHn(value: string, fieldLabel = "HN"): string | null {
  if (!value.trim()) return null;
  const normalized = normalizeNumericIdentifier(value);
  if (!/^[\d๐-๙\s-]+$/.test(value) || normalized.length < 3) {
    return `${fieldLabel} ต้องเป็นตัวเลขอย่างน้อย 3 หลัก`;
  }
  return null;
}

function validatePhone(value: string): string | null {
  if (!value.trim()) return null;
  const normalized = normalizeNumericIdentifier(value);
  if (!/^0\d{8,9}$/.test(normalized)) return "เบอร์โทรต้องเป็นตัวเลข 9-10 หลักและขึ้นต้นด้วย 0";
  return null;
}

function validateDraft(
  draft: DraftState,
  requiresPrivilege: boolean,
  tiers: TierRow[],
): { ok: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  if (!draft.firstName.trim()) errors.firstName = "กรุณาระบุชื่อ";
  if (!draft.lastName.trim()) errors.lastName = "กรุณาระบุนามสกุล";
  const hnError = validateHn(draft.hn);
  if (hnError) errors.hn = hnError;
  const phoneError = validatePhone(draft.phone);
  if (phoneError) errors.phone = phoneError;
  if (draft.nationalId && normalizeNumericIdentifier(draft.nationalId).length !== 13) {
    errors.nationalId = "เลขบัตรประชาชนต้องมี 13 หลัก";
  }
  if (draft.dob && !buddhistInputToIso(draft.dob)) errors.dob = "วันที่ต้องเป็นรูปแบบ วว/ดด/ปปปป";

  if (requiresPrivilege) {
    const donationTotal = Number(draft.donationTotal);
    if (!draft.donationTotal || Number.isNaN(donationTotal) || donationTotal <= 0) {
      errors.donationTotal = "กรุณาระบุยอดบริจาคมากกว่า 0";
    } else if (!tierForDonation(tiers, draft.donationTotal)) {
      errors.donationTotal = "ไม่พบระดับสิทธิสำหรับยอดบริจาคนี้";
    }
    if (!draft.benefitStartDate || !buddhistInputToIso(draft.benefitStartDate)) {
      errors.benefitStartDate = "วันที่เริ่มสิทธิต้องเป็นรูปแบบ วว/ดด/ปปปป";
    }
    if (draft.benefitEndDate && !buddhistInputToIso(draft.benefitEndDate)) {
      errors.benefitEndDate = "วันที่สิ้นสุดต้องเป็นรูปแบบ วว/ดด/ปปปป";
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

function validateMemberDraft(draft: { firstName: string; lastName: string; hn: string; nationalId: string; phone: string; dob: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!draft.firstName.trim()) errors.memberFirstName = "กรุณาระบุชื่อ";
  if (!draft.lastName.trim()) errors.memberLastName = "กรุณาระบุนามสกุล";
  const hnError = validateHn(draft.hn);
  if (hnError) errors.memberHn = hnError;
  if (draft.nationalId && normalizeNumericIdentifier(draft.nationalId).length !== 13) {
    errors.memberNationalId = "เลขบัตรประชาชนต้องมี 13 หลัก";
  }
  const phoneError = validatePhone(draft.phone);
  if (phoneError) errors.memberPhone = phoneError;
  if (draft.dob && !buddhistInputToIso(draft.dob)) errors.memberDob = "วันที่ต้องเป็นรูปแบบ วว/ดด/ปปปป";
  return errors;
}

function toggleSetValue(current: Set<string>, value: string): Set<string> {
  const next = new Set(current);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function formatDateTyping(value: string): string {
  const digits = normalizeNumericIdentifier(value).slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function formatPercent(value: number): string {
  if (value >= 1) return "100%";
  return `${Math.round(value * 100)}%`;
}

function roleLabel(role: "primary" | "supplementary" | null): string {
  if (role === "primary") return "หลัก";
  if (role === "supplementary") return "สมทบ";
  return "ยังไม่มีสิทธิ";
}

function tierStyle(tier: TierSummary | TierRow): CSSProperties {
  return {
    "--tier-bg": tier.color_bg,
    "--tier-border": tier.color_border,
    "--tier-text": tier.color_text,
  } as CSSProperties;
}

function RoleChip({ role }: { role: "primary" | "supplementary" | null }) {
  return <small className={`role-chip ${role ?? "none"}`}>{roleLabel(role)}</small>;
}

function TierChip({ tier }: { tier: TierSummary | null }) {
  if (!tier) return <small className="tier-chip empty">ไม่มีระดับ</small>;
  return (
    <small className="tier-chip" style={tierStyle(tier)}>
      {tier.tier_name}
    </small>
  );
}

function DisclosureButton({
  title,
  meta,
  open,
  onToggle,
}: {
  title: string;
  meta?: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className="disclosure-trigger"
      type="button"
      aria-expanded={open}
      onClick={onToggle}
    >
      <span>
        <strong>{title}</strong>
        {meta ? <small>{meta}</small> : null}
      </span>
      {open ? <ChevronDown size={18} /> : <ExpandRight size={18} />}
    </button>
  );
}

function TierReference({ tiers, open, onToggle }: { tiers: TierRow[]; open: boolean; onToggle: () => void }) {
  return (
    <section className="tier-reference">
      <DisclosureButton
        title="ระดับสิทธิประโยชน์"
        meta="ตารางอ้างอิง"
        open={open}
        onToggle={onToggle}
      />
      {open ? (
      <div className="tier-reference-grid">
        {tiers.map((tier) => (
          <article className="tier-reference-card" key={tier.id} style={tierStyle(tier)}>
            <header>
              <strong>{tier.tier_name}</strong>
              <span>{tier.donation_label}</span>
            </header>
            <dl>
              <div>
                <dt>ส่วนลดห้อง</dt>
                <dd className="discount-lines">
                  <span>
                    <small>ต่ำกว่า 3,000 บาท</small>
                    <strong>{formatPercent(tier.room_discount_under_3000)}</strong>
                  </span>
                  <span>
                    <small>3,000 บาทขึ้นไป</small>
                    <strong>{formatPercent(tier.room_discount_3000_up)}</strong>
                  </span>
                </dd>
              </div>
              <div>
                <dt>ที่จอดรถ</dt>
                <dd>{tier.parking_policy}</dd>
              </div>
              <div>
                <dt>ระยะเวลา</dt>
                <dd>{tier.benefit_duration_label}</dd>
              </div>
              <div>
                <dt>ขอบเขต</dt>
                <dd>{tier.entitlement_scope}</dd>
              </div>
              <div>
                <dt>PMC</dt>
                <dd>{formatPmcPolicy(tier)}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
      ) : null}
    </section>
  );
}

function formatPmcPolicy(tier: TierRow | null): string {
  if (!tier?.pmc_eligible) return "ไม่มีสิทธิ";
  const limit = tier.pmc_annual_limit === null ? "ไม่จำกัดจำนวนครั้ง" : `${tier.pmc_annual_limit} ครั้ง/ปี`;
  return `ยกเว้นค่าธรรมเนียม ${Number(tier.pmc_fee_waiver_amount ?? 0).toLocaleString("th-TH")} บาท · ${limit}`;
}

function setDraftField<K extends keyof DraftState>(
  setDraft: Dispatch<SetStateAction<DraftState>>,
  key: K,
  value: DraftState[K],
) {
  setDraft((current) => ({ ...current, [key]: value }));
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  error,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  placeholder?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
}) {
  return (
    <label className={error ? "field-invalid" : ""}>
      <span>
        {label}
        {required ? <span className="required-marker" aria-label="จำเป็น">*</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-required={required || undefined}
        placeholder={placeholder}
        inputMode={inputMode}
      />
      {error ? <small className="field-error">{error}</small> : null}
    </label>
  );
}

function AdminFeedbackModal({
  kind,
  message,
  fieldErrors,
  onClose,
}: {
  kind: "success" | "error";
  message: string;
  fieldErrors: Record<string, string>;
  onClose: () => void;
}) {
  const uniqueFieldErrors = Array.from(new Set(Object.values(fieldErrors).filter(Boolean)));
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className={`feedback-modal ${kind}`} role="alertdialog" aria-modal="true" aria-labelledby="admin-feedback-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <span className="modal-icon">
            {kind === "success" ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
          </span>
          <div>
            <h3 id="admin-feedback-title">{kind === "success" ? "ทำรายการสำเร็จ" : "ไม่สามารถทำรายการได้"}</h3>
            <p>{message}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} title="ปิด" aria-label="ปิด">
            <X size={18} />
          </button>
        </header>
        {uniqueFieldErrors.length ? (
          <ul>
            {uniqueFieldErrors.map((fieldError) => (
              <li key={fieldError}>{fieldError}</li>
            ))}
          </ul>
        ) : null}
        <button className="primary-action compact" type="button" onClick={onClose}>
          ตกลง
        </button>
      </section>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
  required = false,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}) {
  return (
    <Input
      label={label}
      value={value}
      error={error}
      placeholder="วว/ดด/ปปปป"
      inputMode="numeric"
      required={required}
      onChange={(nextValue) => onChange(formatDateTyping(nextValue))}
    />
  );
}
